import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Typography,
  Button,
  IconButton,
  Box,
  Skeleton,
} from '@mui/material';
import PriceTag from '../ui/PriceTag';
import './MenuItemCard.css';

const AddIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>;
const RemoveIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13H5v-2h14v2z"/></svg>;
const CheckIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>;
const ShoppingCartIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg>;
const FlameIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>;

const SECTION_PLACEHOLDERS = {
  'Hot Coffee': '☕',
  'Cold Drinks': '🧊',
  'Pastries': '🥐',
  'Light Bites': '🥗',
  'Desserts': '🍰',
  'Beverages': '🥤',
  'Starters': '🥗',
  'Main Courses': '🍽️',
  'Appetizers': '🥗',
  'Soups': '🍲',
  'Salads': '🥗',
  'Sides': '🥔',
  'Other': '🍽️',
};

function getSectionEmoji(section) {
  return SECTION_PLACEHOLDERS[section] || '🍽️';
}

/* Grapheme-safe first character. Array.from splits on Unicode code points
   (not UTF-16 units), so Arabic, emoji-name, and surrogate-pair items get a
   sensible single-glyph placeholder. */
function firstGrapheme(name) {
  if (!name) return '?';
  const first = Array.from(name.trim())[0];
  return first ? first.toUpperCase() : '?';
}

/* Card badges. File-local — not a public primitive. Renders at most two
   badges in priority order; out-of-stock pre-empts everything else because
   it changes whether the user can act. Positioned top-inset-inline-start via
   .mic-badges so RTL flips automatically. */
const CardBadges = ({ item, t }) => {
  const badges = [];
  if (item.is_available === false || item.is_available === 0) {
    badges.push({ key: 'stock', label: t('menu.item.outOfStock', 'Out of stock'), variant: 'stock' });
  } else {
    if (item.is_new) badges.push({ key: 'new', label: t('menu.item.new', 'New'), variant: 'new' });
    if (item.is_popular) badges.push({ key: 'popular', label: t('menu.item.popular', 'Popular'), variant: 'popular' });
    if (item.spicy_level > 0) {
      badges.push({
        key: 'spicy',
        label: <><FlameIcon />{item.spicy_level > 1 ? <FlameIcon /> : null}</>,
        variant: 'spicy',
        ariaLabel: t('menu.item.spicy', 'Spicy'),
      });
    }
  }
  if (!badges.length) return null;
  return (
    <div className="mic-badges">
      {badges.slice(0, 2).map(b => (
        <span key={b.key} className={`mic-badge mic-badge--${b.variant}`} aria-label={b.ariaLabel}>
          {b.label}
        </span>
      ))}
    </div>
  );
};

const MenuItemCard = ({
  item,
  currency,
  section,
  getImageUrl,
  onAddToCart,
  onDecreaseQuantity,
  onOpenCustomize,
  cartQuantity = 0,
  // isRTL retained in prop signature for API stability; layout is now
  // direction-driven by logical CSS properties so we no longer read it.
  isRTL: _isRTL,
}) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const imgRef = useRef(null);
  const justAddedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (justAddedTimerRef.current) clearTimeout(justAddedTimerRef.current);
    };
  }, []);

  const hasAddons = item.hasAddons === true;
  const hasSizes = item.hasSizes === true || (Array.isArray(item.sizes) && item.sizes.length > 0);
  // Items with sizes (per-size pricing) need the modal to pick one, just like addons.
  const needsCustomize = hasAddons || hasSizes;
  const fromPrice = hasSizes ? Math.min(...item.sizes.map(s => Number(s.price) || 0)) : null;
  const isUnavailable = item.available_now === false;
  const displayQuantity = cartQuantity;

  // Localized "available only from …" schedule line. The backend sends
  // structured, language-independent availability data ({ status, day, windows })
  // with the clock pre-formatted ("5:00 PM"); we compose the sentence per-locale.
  // Falls back to the legacy English `next_available` string for stale payloads.
  let scheduleText = null;
  const availability = item.availability;
  if (availability && Array.isArray(availability.windows) && availability.windows.length > 0) {
    const ranges = availability.windows.map(w => `${w.start} – ${w.end}`).join(', ');
    if (availability.status === 'upcoming' && availability.day) {
      scheduleText = t('menu.item.availableDayOnly', {
        day: t(`menu.item.days.${availability.day}`, { defaultValue: availability.day }),
        ranges,
        defaultValue: '{{day}} only: {{ranges}}',
      });
    } else {
      scheduleText = t('menu.item.availableOnlyFrom', {
        ranges,
        defaultValue: 'Available only from {{ranges}}',
      });
    }
  } else if (!availability && item.next_available && item.next_available !== 'now'
    && item.is_available !== false && item.is_available !== 0) {
    scheduleText = item.next_available;
  }

  useEffect(() => {
    if (!item.image_url) {
      setImageLoaded(true);
      setImageError(true);
      return;
    }
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth) {
      setImageLoaded(true);
    }
  }, [item.image_url]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isUnavailable) return;
    if (needsCustomize) {
      onOpenCustomize(item);
      return;
    }
    onAddToCart({
      ...item,
      quantity: 1,
      price: parseFloat(item.price),
      selectedAddons: [],
    });
    setJustAdded(true);
    if (justAddedTimerRef.current) clearTimeout(justAddedTimerRef.current);
    justAddedTimerRef.current = setTimeout(() => setJustAdded(false), 1200);
  };

  const handleQuantityChange = (delta) => (e) => {
    e.stopPropagation();
    if (isUnavailable) return;
    if (delta > 0) {
      onAddToCart({
        ...item,
        quantity: 1,
        price: parseFloat(item.price),
        selectedAddons: [],
      });
      setJustAdded(true);
      if (justAddedTimerRef.current) clearTimeout(justAddedTimerRef.current);
      justAddedTimerRef.current = setTimeout(() => setJustAdded(false), 800);
    } else if (onDecreaseQuantity) {
      onDecreaseQuantity(item);
    }
  };

  const imageUrl = item.image_url ? getImageUrl(item.image_url) : null;
  const showPlaceholder = !imageUrl || imageError;
  const placeholderEmoji = getSectionEmoji(section);
  const placeholderInitial = firstGrapheme(item.name);

  /* Whole-card click only opens the customize modal when the item has
     addons. For simple items, the body is non-interactive (disableRipple +
     no onClick) so the explicit "Add to cart" button is the only target.
     Clearer affordance. */
  const bodyInteractive = needsCustomize && !isUnavailable;

  return (
    <Card
      className={`mic-card${isUnavailable ? ' mic-card--unavailable' : ''}`}
      elevation={0}
    >
      <CardActionArea
        component="div"
        onClick={bodyInteractive ? () => onOpenCustomize(item) : undefined}
        disableRipple={!bodyInteractive}
        focusRipple={bodyInteractive}
        sx={{
          flex: '1 1 auto',
          display: 'block',
          cursor: bodyInteractive ? 'pointer' : 'default',
        }}
      >
        <Box className="mic-card__media">
          {showPlaceholder ? (
            <div className="mic-card__placeholder" role="img" aria-label={item.name}>
              <span className="mic-card__placeholder-glyph" aria-hidden="true">{placeholderEmoji}</span>
              <span className="mic-card__placeholder-initial" aria-hidden="true">{placeholderInitial}</span>
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <Skeleton
                  variant="rectangular"
                  sx={{ position: 'absolute', inset: 0 }}
                  animation="wave"
                />
              )}
              <img
                ref={imgRef}
                src={imageUrl}
                alt={item.name}
                loading="lazy"
                onLoad={handleImageLoad}
                onError={handleImageError}
                className="mic-card__image"
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            </>
          )}
          <CardBadges item={item} t={t} />
        </Box>

        <CardContent className="mic-card__body">
          <Typography component="h3" className="mic-card__name">
            {item.name}
          </Typography>
          {item.description && (
            <Typography component="p" className="mic-card__description">
              {item.description}
            </Typography>
          )}
          {scheduleText && (
            <span className="mic-card__schedule" aria-label={t('menu.item.nextAvailable', 'Next available')}>
              <span aria-hidden="true">🕒</span> {scheduleText}
            </span>
          )}
          <div className="mic-card__price-row">
            <span className="mic-card__price">
              {hasSizes && (
                <span className="mic-card__price-from">{t('menu.item.from', 'from')} </span>
              )}
              <PriceTag
                amount={hasSizes ? fromPrice : item.price}
                currency={currency}
                color="inherit"
                weight="medium"
                size="md"
                style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
              />
            </span>
          </div>
        </CardContent>
      </CardActionArea>

      <CardActions className="mic-card__actions">
        {needsCustomize ? (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className="mic-add-btn"
            startIcon={<ShoppingCartIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onOpenCustomize(item);
            }}
            disabled={isUnavailable}
            aria-label={t('menu.customize')}
          >
            {t('menu.customize')}
          </Button>
        ) : displayQuantity > 0 ? (
          <Box
            className="mic-stepper"
            role="group"
            aria-label={t('menu.cart.quantityFor', { item: item.name, defaultValue: 'Quantity for {{item}}' })}
          >
            <IconButton
              className="mic-stepper__btn"
              onClick={handleQuantityChange(-1)}
              disabled={isUnavailable}
              aria-label={t('menu.cart.decreaseQuantity')}
            >
              <RemoveIcon />
            </IconButton>
            <span className="mic-stepper__qty" aria-live="polite">{displayQuantity}</span>
            <IconButton
              className="mic-stepper__btn"
              onClick={handleQuantityChange(1)}
              disabled={isUnavailable}
              aria-label={t('menu.cart.increaseQuantity')}
            >
              <AddIcon />
            </IconButton>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={justAdded ? <CheckIcon /> : <AddIcon />}
            onClick={handleAddClick}
            disabled={isUnavailable}
            aria-label={t('menu.addToCart')}
            className={`mic-add-btn${justAdded ? ' mic-add-btn--added' : ''}`}
          >
            {justAdded ? t('menu.item.added', 'Added!') : t('menu.addToCart')}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default MenuItemCard;
